/**
 * KAN-746: карточка заказа интернет-магазина. Шапка: номер, статус, клиент,
 * менеджер, кнопки переходов по правам. Позиции из каталога, при праве
 * правки: добавить товар поиском по каталогу, изменить количество, удалить.
 * Итоги (подытог, доставка, наценка, итого) и адрес доставки.
 * Ошибки правок и переходов (400/403/409) показываются текстом над позициями.
 * KAN-759: правка в useOrderEditor, позиции и итоги в OrderItemsPanel (общие
 * с модалками заказа CRM).
 */
import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, MapPin, ShoppingCart } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EstimateManagerSelect } from '@/components/estimates/EstimateManagerSelect';
import { OrderActions } from '@/components/orders/OrderActions';
import { OrderErrorAlert, OrderItemsPanel } from '@/components/orders/OrderItemsPanel';
import { formatOrderDate, orderStatusView, orderTitle } from '@/components/orders/orderFormat';
import { isNotFoundError } from '@/hooks/useCatalog';
import { useOrder } from '@/hooks/useOrders';
import { useOrderEditor } from '@/hooks/useOrderEditor';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import type { OrderDetail } from '@/types/order';

const ORDERS_CRUMB = { label: 'Заказы', href: '/orders' };

function OrderBody({ order }: { order: OrderDetail }) {
  const editor = useOrderEditor(order.id);
  const status = orderStatusView(order.status);

  return (
    <PageLayout
      data-testid="order-detail-page"
      title={orderTitle(order.short_id)}
      breadcrumbs={[ORDERS_CRUMB, { label: orderTitle(order.short_id) }]}
      backButton={{ href: '/orders' }}
      stickyHeader={false}
      subtitle={
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
          <Badge variant={status.variant} data-testid="order-status">
            {status.label}
          </Badge>
          <span>
            Клиент:{' '}
            {order.client ? (
              <Link
                to={`/clients/${order.client.id}`}
                className="text-blue-600 hover:underline"
                data-testid="order-client-link"
              >
                {order.client.name || 'Без имени'}
                {order.client.company ? `, ${order.client.company}` : ''}
              </Link>
            ) : (
              'не указан'
            )}
          </span>
          <span>Создан: {formatOrderDate(order.created_at)}</span>
          {order.submitted_at && <span>Оформлен: {formatOrderDate(order.submitted_at)}</span>}
        </div>
      }
      actions={
        <OrderActions
          permissions={order.permissions}
          pending={editor.pending}
          onAction={(action) => editor.runAction(action)}
        />
      }
    >
      <div className="space-y-6">
        <EstimateManagerSelect estimateId={order.id} manager={order.manager} createdBy={order.created_by} />

        <OrderErrorAlert error={editor.error} />

        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
            <CardTitle className="flex items-center gap-2 text-lg">
              <ShoppingCart className="h-5 w-5" />
              Позиции
            </CardTitle>
            {editor.pending && <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-label="Сохранение" />}
          </CardHeader>
          <CardContent className="p-0">
            <OrderItemsPanel order={order} editor={editor} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4" />
              Адрес доставки
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-line text-sm text-gray-700" data-testid="order-address">
              {order.address || 'Не указан'}
            </p>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}

export function OrderDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const query = useOrder(id);
  const order = query.data;

  useEffect(() => {
    document.title = `${order ? orderTitle(order.short_id) : 'Заказ'} - SMETA AI`;
  }, [order]);

  if (order) return <OrderBody order={order} />;

  return (
    <PageLayout
      data-testid="order-detail-page"
      title="Заказ"
      breadcrumbs={[ORDERS_CRUMB, { label: 'Заказ' }]}
      backButton={{ href: '/orders' }}
    >
      {query.isLoading ? (
        <div className="flex justify-center py-12" data-testid="order-loading">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="rounded-lg border bg-white p-6 text-sm text-gray-600" data-testid="order-not-found">
          {isNotFoundError(query.error)
            ? 'Заказ не найден или недоступен.'
            : extractErrorMessage(query.error, 'Не удалось загрузить заказ')}
        </div>
      )}
    </PageLayout>
  );
}

export default OrderDetailPage;
